import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { APIData } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CustomQuerySetupApiMetricBuilderMethodAndUrlComponent from 'webapp/components/custom-query/setup/api-metric-builder/method-and-url';
import CustomQuerySetupApiMetricBuilderPaginationComponent from 'webapp/components/custom-query/setup/api-metric-builder/pagination';
import CustomQuerySetupApiMetricBuilderTabsComponent from 'webapp/components/custom-query/setup/api-metric-builder/tabs';
import QueryBuilderFieldMetricDefinitionComponent from 'webapp/components/query-builder/field/metric-definition';

import type CustomMetricSetupFormApiMetricBuilder from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder';

interface Args {
	form: CustomMetricSetupFormApiMetricBuilder;
	isDisabled: boolean;
	theme: string;
	groupAdvanced: boolean;
	onContentChange: () => void;
	goNext?: () => void;
}

export default class CustomQuerySetupApiMetricBuilderGeneralSettingsComponent extends Component<Args> {
	@tracked isAdvancedOpen = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.isAdvancedOpen = !this.args.groupAdvanced;
	}

	get isDisabled(): boolean {
		return this.args.isDisabled || this.args.form.isPreviewButtonLoading;
	}

	get showPagination(): boolean {
		return this.args.form.hasPagination;
	}

	get source() {
		return this.args.form.query.sourceObj!;
	}

	@action
	toggleAdvanced(): void {
		this.isAdvancedOpen = !this.isAdvancedOpen;
		this.args.onContentChange();
	}

	@action
	updateMetricDefinition(value: APIData.CustomQuery.MetricDefinitionValue): void {
		this.args.form.metricDefinition = value;
	}
	<template>
		<CustomQuerySetupApiMetricBuilderMethodAndUrlComponent
			@form={{@form}}
			@isDisabled={{this.isDisabled}}
			@theme={{@theme}}
			@goNext={{@goNext}}
		/>

		<div class="item-group">
			<div class="row group {{if this.isAdvancedOpen 'open'}}">
				{{#if @groupAdvanced}}
					<a role="button" class="toggler" {{on "click" this.toggleAdvanced}}>
						<CommonUiIconComponent @name="ChevronDown" @size="sm" />
						Advanced options
					</a>
				{{/if}}
				<div class="items">
					<CustomQuerySetupApiMetricBuilderTabsComponent
						@form={{@form}}
						@isDisabled={{this.isDisabled}}
						@onContentChange={{@onContentChange}}
						@theme={{@theme}}
					/>

					{{#if this.showPagination}}
						<CustomQuerySetupApiMetricBuilderPaginationComponent
							@form={{@form}}
							@isDisabled={{this.isDisabled}}
							@theme={{@theme}}
						/>
					{{/if}}

					<div class="item item--slim">
						<QueryBuilderFieldMetricDefinitionComponent
							@model={{@form.metricDefinition}}
							@builderType=""
							@source={{this.source}}
							@isDisabled={{this.isDisabled}}
							@onSave={{this.updateMetricDefinition}}
						/>
					</div>
				</div>
			</div>
		</div>
	</template>
}
